import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CTAs from "../../components/CTAs";
import NameInput from "../../components/NameInput";
import PasswordInputFieldRegister from "../../components/PasswordInputFieldRegister";

const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    verifyInvitation();
  }, []);

  const verifyInvitation = async () => {
    if (!token || !email) {
      setError("Invalid invitation link");
      setIsVerifying(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/subscription/verify-invitation?token=${token}&email=${email}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Invalid invitation");
      }

      setInvitationData(data.invitation);
      setIsVerifying(false);
    } catch (error) {
      console.error("Verification error:", error);
      setError(error.message);
      setIsVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert("Please enter your name");
      return;
    }
    if (!password.trim()) {
      alert("Please enter a password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/subscription/accept-invitation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, email, name, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to accept invitation");
      }

      // Show success state and redirect to login
      setSuccess(true);
    } catch (error) {
      console.error("Accept invitation error:", error);
      alert(`Error: ${error.message}`);
      setIsLoading(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-neutrals-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-primary-500 mx-auto mb-4"></div>
          <p className="text-neutrals-neutral-300">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-neutrals-neutral-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-semantics-high-high-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-semantics-high-high-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Invalid Invitation</h2>
          <p className="text-neutrals-neutral-300 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="bg-primary-primary-500 text-white px-6 py-2 rounded-lg"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Success state ──────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-neutrals-neutral-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Registration Complete!</h2>
          <p className="text-neutrals-neutral-300 mb-6">
            Your account has been set up successfully. Please log in to continue.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-primary-primary-500 text-white px-6 py-2 rounded-lg w-full"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-neutrals-neutral-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <img
            className="w-[120px] h-[100px] mx-auto mb-4"
            alt="Logo"
            src="assets/main-logo.svg"
          />
          <h1 className="text-3xl font-bold mb-2">Complete Your Registration</h1>
          <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            <p className="text-sm text-neutrals-neutral-300 mb-1">
              You've been invited to join
            </p>
            <p className="text-xl font-semibold text-primary-primary-500">
              {invitationData?.companyName}
            </p>
            <p className="text-sm text-neutrals-neutral-300 mt-2">
              by {invitationData?.inviterName}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <p className="text-sm text-neutrals-neutral-300 mb-4">
                Email: <strong>{email}</strong>
              </p>
              <div className="space-y-4">
                <NameInput
                  title="Your Name"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  width="100%"
                />
                <PasswordInputFieldRegister
                  title="Create Password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <CTAs
                type="Gradientbtn"
                text={isLoading ? "Processing..." : "Complete Registration"}
                textColor="text-neutrals-neutral-50"
                bgColor="bg-primary-primary-500"
                borderColor="border-primary-primary-500"
                width="w-full"
                onClick={handleSubmit}
                disabled={isLoading}
              />
              <p className="text-xs text-neutrals-neutral-300 text-center mt-4">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-sm text-neutrals-neutral-300">
            Invitation expires:{" "}
            {new Date(invitationData?.expiresAt).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;